import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useTable from 'components/table/hooks/useTable';
import { createColumnHelper } from '@tanstack/react-table';
import { useIsFetching } from '@tanstack/react-query';
import { INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY } from 'components/domain/customData/fields/internal/useFetchInternalCustomDataFields';
import {
  DisplayPreArrivalFormField,
  InformationMap,
} from '../../PreArrivalFormTab.types';
import {
  CollapseSectionStyled,
  SectionTableStyled,
} from '../common/Common.styles';
import DisplayTypeCell from '../common/cells/DisplayTypeCell';
import DisplayNameCell from '../common/cells/DisplayNameCell';
import FieldValidationCell from './cells/FieldValidationCell';
import StatusCell from './cells/StatusCell';
import AddCustomFieldButton from './add/AddCustomFieldButton';

const ExtraCustomSection = ({
  extraInformationData,
  customFields,
}: {
  extraInformationData: InformationMap | undefined;
  customFields: Record<string, DisplayPreArrivalFormField>;
}) => {
  const isFetchingData =
    useIsFetching({
      queryKey: [INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY],
    }) > 0;
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<DisplayPreArrivalFormField>();
  const { watch } = useFormContext();
  const newCustomFields = watch('newCustomFields');

  const columns = [
    columnHelper.display({
      id: 'status',
      cell: StatusCell,
    }),
    columnHelper.display({
      id: 'displayName',
      header: t('pageAutomations.preArrivalForm.informationTable.fieldName'),
      cell: DisplayNameCell,
    }),
    columnHelper.display({
      id: 'displayType',
      header: t('pageAutomations.preArrivalForm.informationTable.fieldType'),
      cell: DisplayTypeCell,
    }),
    columnHelper.display({
      id: 'fieldValidation',
      header: t(
        'pageAutomations.preArrivalForm.informationTable.fieldValidation',
      ),
      meta: {
        showCellTooltip: false,
      },
      cell: FieldValidationCell,
    }),
  ];

  const newFieldsTrigger = JSON.stringify(newCustomFields);

  const orderedTableData = useMemo(() => {
    // @ts-expect-error
    const data: DisplayPreArrivalFormField[] = [
      ...Object.values(extraInformationData || {}),
      ...Object.values(customFields || {}),
      ...Object.values(newCustomFields || {}),
    ];

    return data;
  }, [extraInformationData, customFields, newFieldsTrigger]);

  const tableInstance = useTable<DisplayPreArrivalFormField>({
    columns,
    data: orderedTableData,
  });

  return (
    <CollapseSectionStyled
      variant="card"
      label={t(
        'pageAutomations.preArrivalForm.sectionHeaders.extraInformation',
      )}
      defaultExpanded
      data-testid="pre-arrival-form-section-extra-custom"
      buttons={<AddCustomFieldButton />}
    >
      <SectionTableStyled
        // key={JSON.stringify(newCustomFields)}
        isLoading={isFetchingData}
        tableInstance={tableInstance}
      />
    </CollapseSectionStyled>
  );
};

export default ExtraCustomSection;
